import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input, Button, Switch } from '@/components/ui';

import { useSystemSettings } from '../hooks/useSystemSettings';

const securitySchema = z.object({
  passwordMinLength: z.number().min(6, "Tối thiểu 6 ký tự").max(32),
  requireSpecialChar: z.boolean(),
  requireNumber: z.boolean(),
  require: z.boolean(),
  maxActiveSessions: z.number().min(1).max(10),
  sessionTimeout: z.number().min(15, "Tối thiểu 15 phút"),
  force2FA: z.boolean(),
});

type SecurityFormValues = z.infer<typeof securitySchema>;

interface SecuritySectionProps {
  onChange?: (isDirty: boolean) => void;
  onSaveSuccess?: () => void;
}

export function SecuritySection({ onChange, onSaveSuccess }: SecuritySectionProps) {
  const { security, updateSecurity } = useSystemSettings();

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<SecurityFormValues>({
    resolver: zodResolver(securitySchema),
    defaultValues: security || {}
  });

  // Sync data when loaded
  React.useEffect(() => {
    if (security) {
      reset(security);
    }
  }, [security, reset]);

  React.useEffect(() => {
    if (onChange) {
      onChange(isDirty);
    }
  }, [isDirty, onChange]);

  const onSubmit = async (data: SecurityFormValues) => {
    try {
      await updateSecurity(data);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      // Error handled by mutation
    }
  };

  return (
    <form id="security-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Password Policy */}
      <div>
        <h4 className="text-sm font-black text-slate-900  tracking-wider mb-4 flex items-center gap-2">
          <i className="ri-lock-password-line text-brand-600" />
          Chính sách mật khẩu
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 ">Độ dài tối thiểu</label>
            <Input
              {...register('passwordMinLength', { valueAsNumber: true })}
              type="number"
              className={errors.passwordMinLength ? 'border-red-500' : ''}
            />
            {errors.passwordMinLength && <p className="text-[10px] text-red-500 font-bold">{errors.passwordMinLength.message}</p>}
          </div>

          <div className="flex flex-col gap-4 justify-center">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Yêu cầu ký tự đặc biệt</span>
              <input type="checkbox" {...register('requireSpecialChar')} className="w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Yêu cầu chữ số</span>
              <input type="checkbox" {...register('requireNumber')} className="w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Yêu cầu chữ hoa</span>
              <input type="checkbox" {...register('require')} className="w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Session Management */}
      <div>
        <h4 className="text-sm font-black text-slate-900  tracking-wider mb-4 flex items-center gap-2">
          <i className="ri-timer-line text-brand-600" />
          Quản lý phiên làm việc
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 ">Số thiết bị tối đa</label>
            <Input
              {...register('maxActiveSessions', { valueAsNumber: true })}
              type="number"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 ">Thời gian hết hạn (phút)</label>
            <Input
              {...register('sessionTimeout', { valueAsNumber: true })}
              type="number"
            />
          </div>
        </div>
      </div>

      {/* Advanced Security */}
      <div>
        <h4 className="text-sm font-black text-slate-900  tracking-wider mb-4 flex items-center gap-2">
          <i className="ri-shield-flash-line text-brand-600" />
          Bảo mật nâng cao
        </h4>
        <div className="bg-brand-50/30 p-6 rounded-2xl border border-brand-100 flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-bold text-brand-900">Bắt buộc xác thực 2 lớp (2FA)</p>
            <p className="text-xs text-brand-600/70">Tất cả người dùng quản trị phải thiết lập 2FA để đăng nhập.</p>
          </div>
          <input type="checkbox" {...register('force2FA')} className="w-6 h-6 rounded border-brand-200 text-brand-600 focus:ring-brand-600" />
        </div>
      </div>
    </form>
  );
}
